import { useState } from "react";
import { C } from "../../../lib/colors.js";
import { checkAgainText } from "../../../lib/forecast/answerCheck.js";
import { useDeadlineClock } from "../useDeadlineClock.js";
import { ui } from "../../../lib/telemetry/instrument.js";
import { Button, Spinner } from "../../../components/ui/Primitives.jsx";
import { IconChevronDown, IconChevronRight, IconRefresh } from "../../../components/ui/Icons.jsx";

/** The "?" a closed question with no answer carries, in the header and on the timeline. */
export function AnswerUnknownMark({ size = 18 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        width: size,
        height: size,
        borderRadius: "50%",
        border: `1.5px solid ${C.grayL}`,
        color: C.grayL,
        fontSize: Math.round(size * 0.62),
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      ?
    </span>
  );
}

/**
 * Where the search for a closed question's answer stands: when Sayer last looked, what would
 * settle it, what it found, and "Check now" for a viewer who may edit the forecast.
 *
 * @param {object} props
 * @param {NonNullable<ReturnType<typeof import("../../../lib/forecast/answerCheck.js").answerCheckState>>} props.state
 * @param {{enabled: boolean, reason: string|null}} props.checkNow
 * @param {(() => Promise<{status: number, body: any}|null>)|undefined} props.onCheckNow asks the
 *   resolver now and resolves to the server's refusal, if any; absent for a viewer who may not
 *   edit
 * @param {number} props.nowMs the app's now, for when a refused press may be retried
 * @param {string} props.id the forecast id, for telemetry
 * @param {object} [props.style] placement, merged over the panel's own box
 */
export default function AnswerCheckPanel({ state, checkNow, onCheckNow, nowMs, id, style }) {
  const [showChecked, setShowChecked] = useState(false);
  // Why the last press did not start a check: {text, until}. A refusal for the hourly limit holds
  // the button until the server's time (`until`); another tab may have taken the hour.
  const [refused, setRefused] = useState(null);
  // A press in progress, until the check it started ends: a second press would start another.
  const [pressing, setPressing] = useState(false);
  const checking = state.kind === "checking_now";
  const again = state.kind === "no_answer";
  const clockMs = useDeadlineClock(refused?.until ?? null);
  const held = refused?.until != null && Math.max(nowMs, clockMs) < refused.until;
  const reason =
    checkNow.reason || (refused && (refused.until == null || held) ? refused.text : null);
  const check = async () => {
    if (pressing) return;
    setRefused(null);
    setPressing(true);
    let refusal;
    try {
      refusal = await onCheckNow();
    } catch (err) {
      console.error("[answer-check] check failed", err);
      refusal = { status: 0, body: null };
    } finally {
      setPressing(false);
    }
    if (!refusal) return;
    const at = Date.parse(refusal.body?.available_at || "");
    setRefused(
      refusal.status === 429 && Number.isFinite(at)
        ? { text: checkAgainText(at), until: at }
        : { text: "Sayer couldn't check right now. Try again later.", until: null },
    );
  };
  return (
    <section
      data-answer-check={state.kind}
      aria-label="Answer for this question"
      style={{
        marginTop: 14,
        padding: "12px 14px",
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        background: `color-mix(in srgb, ${C.grayL} 4%, ${C.bg})`,
        maxWidth: 880,
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <span style={{ paddingTop: 1 }}>{checking ? <Spinner /> : <AnswerUnknownMark />}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, color: C.white, lineHeight: 1.45 }}>{state.detail}</div>
          {state.next && (
            <div style={{ marginTop: 2, fontSize: 13, color: C.gray }}>{state.next}</div>
          )}
          {onCheckNow && !checking && reason && (
            <div style={{ marginTop: 2, fontSize: 12, color: C.gray }}>{reason}</div>
          )}
        </div>
        {onCheckNow && state.kind !== "found" && (
          <Button
            {...ui("timeline.check-answer", id)}
            icon={<IconRefresh />}
            disabled={!checkNow.enabled || pressing || held}
            title={reason || "Ask Sayer to look for the answer now"}
            onClick={check}
            style={{ flex: "none" }}
          >
            {checking ? "Checking…" : again ? "Check again" : "Check now"}
          </Button>
        )}
      </div>

      {state.missing.length > 0 && (
        <div style={{ marginTop: 12, paddingLeft: 30 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.gray, letterSpacing: 0.2 }}>
            What would settle it
          </div>
          {state.missing.map((m) => (
            <div key={m} style={{ marginTop: 3, fontSize: 13, color: C.grayL, lineHeight: 1.45 }}>
              {m}
            </div>
          ))}
        </div>
      )}

      {state.checked.length > 0 && (
        <div style={{ marginTop: 10, paddingLeft: 30 }}>
          <button
            type="button"
            {...ui("timeline.answer-check-findings", id)}
            aria-expanded={showChecked}
            onClick={() => setShowChecked((v) => !v)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: 0,
              border: "none",
              background: "none",
              color: C.gray,
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            {showChecked ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            What Sayer found ({state.checked.length})
          </button>
          {showChecked && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {state.checked.map((c) => (
                <li
                  key={c}
                  style={{ marginTop: 3, fontSize: 13, color: C.grayL, lineHeight: 1.45 }}
                >
                  {c}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
